/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const MIN_FILL_HEIGHT = 240;

export const FILL_HEIGHT_GUTTER = 16;

const HEIGHT_CHANGE_TOLERANCE = 1;

const OSCILLATION_WINDOW_MS = 250;

/**
 * Measures how much viewport height is left below an element and returns it as a
 * max-height, so a table fills the remaining screen space and scrolls internally
 * instead of being capped at a fixed pixel value.
 *
 * Measured synchronously on commit, then recomputed on viewport resize and on any
 * layout change above or below the element (filter panels, row-count changes,
 * sidebar collapse) via a ResizeObserver.
 *
 * @param {object} options
 * @param {React.RefObject} options.containerRef Element that should fill the space.
 * @param {React.RefObject} [options.bottomRef] Block rendered below it (pagination etc.), reserved from the total.
 * @param {boolean} [options.enabled] Set false to opt out and keep natural height.
 * @param {number} [options.minHeight] Floor applied to the computed height.
 * @returns {number|undefined} Available height in px, or undefined when it cannot be measured.
 */
export const useFillViewportHeight = ({
  containerRef,
  bottomRef,
  enabled = true,
  minHeight = MIN_FILL_HEIGHT,
} = {}) => {
  const [maxHeight, setMaxHeight] = useState(undefined);
  const heightRef = useRef(undefined);
  const previousHeightRef = useRef(undefined);
  const changedAtRef = useRef(0);
  const settleTimerRef = useRef(null);
  const recheckingRef = useRef(false);
  const measureRef = useRef(null);

  const applyHeight = useCallback((height, now = Date.now()) => {
    if (height === heightRef.current) return;
    previousHeightRef.current = heightRef.current;
    heightRef.current = height;
    changedAtRef.current = now;
    setMaxHeight(height);
  }, []);

  const measure = useCallback(() => {
    const container = containerRef?.current;

    if (!enabled || !container || typeof window === "undefined") {
      applyHeight(undefined);
      return;
    }

    if (container.offsetParent === null) return;

    const viewportHeight = window.innerHeight;
    if (!viewportHeight) return;

    // Measure in the unscrolled layout: pages scroll inside containers, and the applied height
    // shifts their scroll position. html's scrollTop is the window's, so the walk stops below it.
    let scrolledBy = window.scrollY || 0;
    for (let node = container.parentElement; node && node !== document.documentElement; node = node.parentElement) {
      scrolledBy += node.scrollTop || 0;
    }
    const distanceFromTop = container.getBoundingClientRect().top + scrolledBy;
    const reservedBelow = bottomRef?.current?.offsetHeight || 0;
    const available = viewportHeight - distanceFromTop - reservedBelow - FILL_HEIGHT_GUTTER;

    const next = Math.max(Math.round(available), minHeight);
    const current = heightRef.current;
    // Sub-pixel layout at non-100% zoom can flip the rounded value back and forth.
    if (current !== undefined && Math.abs(current - next) <= HEIGHT_CHANGE_TOLERANCE) {
      recheckingRef.current = false;
      return;
    }

    // A flip straight back to the previous height within the window is treated as a loop
    // between two heights; settle on the smaller one instead of re-rendering forever.
    const now = Date.now();
    const revertsRecentChange =
      previousHeightRef.current !== undefined &&
      Math.abs(previousHeightRef.current - next) <= HEIGHT_CHANGE_TOLERANCE &&
      now - changedAtRef.current < OSCILLATION_WINDOW_MS;
    if (revertsRecentChange && next >= current) {
      // Re-check once the window has passed, so a real change undone quickly doesn't leave the
      // table short; a re-check that hits the loop again stays settled.
      if (!recheckingRef.current) {
        window.clearTimeout(settleTimerRef.current);
        settleTimerRef.current = window.setTimeout(() => {
          settleTimerRef.current = null;
          recheckingRef.current = true;
          measureRef.current();
        }, OSCILLATION_WINDOW_MS - (now - changedAtRef.current) + 1);
      }
      recheckingRef.current = false;
      return;
    }

    applyHeight(next, now);
  }, [containerRef, bottomRef, enabled, minHeight, applyHeight]);

  // The re-check calls the latest committed `measure`, not the one that armed it.
  useLayoutEffect(() => {
    measureRef.current = measure;
  });

  useEffect(() => () => {
    if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
  }, []);

  // Runs after every commit rather than only when `measure` changes: the container is
  // often rendered behind an async loading gate, so the ref is still null on mount and a
  // dependency-gated effect would never see it attach. `measure` skips unchanged (within 1px)
  // values and settles a loop between two heights whose passes take under 250 ms.
  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    let frame = null;
    let pending = false;
    const scheduleMeasure = () => {
      if (pending) return;
      pending = true;
      frame = window.requestAnimationFrame(() => {
        pending = false;
        measure();
      });
    };

    window.addEventListener("resize", scheduleMeasure);

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(scheduleMeasure);
      if (document.body) observer.observe(document.body);
      if (containerRef?.current) observer.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", scheduleMeasure);
      if (pending && frame !== null && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [measure, enabled, containerRef]);

  return maxHeight;
};

export default useFillViewportHeight;
